import { isNil, omit } from 'lodash-es';
import { ImResponse, resolve, storeHomeV2Client } from '..';
import { PermissionV2 } from './permissions';

export interface RoleWithDepartment {
  id: number;
  name: string;
  description: string;
  appliedCount: number;
  assignConversation: boolean;
  isDefault?: Java.NumberBool;
  permissionWithScopes: {
    permissionId: number;
    permissionName: General.PermissionName;
    permissionGroupName: General.PermissionGroupName;
    platformStoreScopes: General.StoreScope[];
  }[];
  assignConversationConfig: {
    assignConversation: boolean;
    platformStoreScopes: General.StoreScope[];
    substituteType: 'NO_SUBSTITUTE' | 'SPECIFIC_ROLE' | 'ALL_ROLE';
    receptionScope: 'ALL_BUYER' | 'TRADE_STATUS' | 'BUYER_LABEL';
    substituteRoleId: number;
    tradeStatusFilters: string[];
    buyerFilters: string[];
    assignBuyerAndOrderRule: {
      isAssignAfterSalesOrder: boolean;
      isAssignNegativeReview: boolean;
    };
    receptionConfigs: ('SHOPEE_BUYER' | 'SHOPEE_AFFILIATE')[];
  };
  rolePermScopeType: 'ALL_PERM_ALIGNED' | 'DIFF_BY_PERM';
}

export const getRoleDetail = (roleId: RoleWithDepartment['id']) => {
  return storeHomeV2Client.get<ImResponse<RoleWithDepartment>>(`/admin/roles/${roleId}`);
};
export interface CreateRoleBody
  extends Omit<
    RoleWithDepartment,
    'id' | 'appliedCount' | 'assignConversationConfig' | 'assignConversation'
  > {
  copyRoleId?: number;
  departmentId: number;
  assignConversationConfig: Partial<RoleWithDepartment['assignConversationConfig']>;
}

export const SPECIAL_PREMISSIONS_ID = {
  管理员视角: 2,
};
export const isNeedAssignConversationConfig = (permissionIds: PermissionV2['permissionId'][]) => {
  return permissionIds.every((p) => p !== SPECIAL_PREMISSIONS_ID.管理员视角);
};
const dealWithAssignConversationConfig = (body: CreateRoleBody | UpdateRoleBody) => {
  if (isNil(body?.assignConversationConfig)) {
    return { ...body, assignConversationConfig: { assignConversation: false } };
  }
  return body;
};
export const createRole = (body: CreateRoleBody) => {
  return storeHomeV2Client.post('/admin/roles', dealWithAssignConversationConfig(body));
};

export interface UpdateRoleBody extends Partial<Omit<CreateRoleBody, 'departmentId'>> {
  id: RoleWithDepartment['id']; // 与 role 具体 返回值保持一致方便表单定义、操作
  // roleId: RoleWithDepartment['id']; // 后端内部调用使用的值，前端可以不提供 @潘建军
}
export const updateRole = (body: UpdateRoleBody) => {
  return storeHomeV2Client.put(
    `/admin/roles/${body.id}`,
    // 因后端接口定义变动，提交时omit id 保持与接口定义一致
    omit(dealWithAssignConversationConfig(body), 'id'),
  );
};

export const deleteRole = (roleId: RoleWithDepartment['id']) => {
  return storeHomeV2Client.delete(`/admin/roles/${roleId}`);
};

export interface Role {
  id: number;
  isDefault: 0 | 1;
  name: string;
  description: string;
  assignConversationConfig: RoleWithDepartment['assignConversationConfig'];
  permissionWithScopes: {
    permissionId: number;
    permissionName: General.PermissionName;
    permissionGroupName: string;
    platformStoreScopes: General.StoreScope[];
  }[];
}

export const getUserRoles = (signal?: AbortSignal) => {
  return resolve(
    storeHomeV2Client.get<ImResponse<{ roles: Role[] }>>('/admin/user/roles', { signal }),
  );
};

/**
 * 查询部门中参与咨询分配的角色列表
 * @param departmentId
 * @returns
 */
export const getReceptionRolesList = (departmentId: StoreHomeV2.Department['id'] | string) => {
  return resolve(
    storeHomeV2Client.get<ImResponse<{ roles: StoreHomeV2.ReceptionGroup[] }>>(
      `/admin/departments/${departmentId}/roles_assign_conversation`,
    ),
  );
};

export interface AppendRoleReceptionStoresBody {
  roleIds: Array<StoreHomeV2.RoleWithDepartment['id']>;
  platformStores: Array<{
    platform: General.PlatformUpperCaseTunnel;
    storeIds: Array<StoreHomeV2.NoReceptionStore['id']>;
  }>;
}
/**
 * 为未配置咨询分配角色的店铺额外进行角色绑定配置
 * @param departmentId
 * @returns
 */
export const appendRoleReceptionStores = (body: AppendRoleReceptionStoresBody) => {
  return storeHomeV2Client.post(`/admin/roles/append_reception_stores`, body);
};
